import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { acoesApi, type Acao, type StatusAcao } from '@/lib/api/acoes';
import { contatosApi } from '@/lib/api/contatos';
import { emendasApi } from '@/lib/api/emendas';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { useTemas } from '@/lib/hooks/useTemas';
import { toast } from '@/lib/stores/toastStore';
import { formatarData } from '@/lib/format';
import { Drawer } from '@/design-system/Drawer';
import type { BairroView } from '@/lib/hooks/useBairros';
import {
  ROTULO_BANCO,
  STATUS_BANCO,
  STATUS_VISIVEIS,
  derivarStatus,
  rotuloBanco,
  rotuloVisivel,
  type StatusVisivel,
} from './statusDerivado';
import { Info } from '@/design-system/Info';

const TIPOS = ['Obra', 'Emenda', 'Projeto de lei', 'Indicação', 'Evento', 'Visita', 'Ofício'];

const brl = (v: number) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const num = (v: number) => v.toLocaleString('pt-BR');

/**
 * "Moradores" com lastro real.
 *
 * O KPI do protótipo é `Moradores alcançados`, somando um campo digitado por
 * ação. Um número digitado não é um alcance medido: ninguém sabe se é real, e
 * exibi-lo como indicador é inventar informação com cara de medição. O que o
 * sistema realmente tem é `terr.bairro.populacao`, importado do IBGE
 * (`apps/api/src/terr/ibge/ibge-import.ts`) — população do território, não
 * gente comprovadamente atingida. Por isso soma-se a população dos territórios
 * DISTINTOS com ação (somar por ação contaria o mesmo bairro duas vezes) e o
 * rótulo muda junto: chamar isso de "alcançados" afirmaria o que o código não
 * faz. Ações canceladas não contam — não alcançaram ninguém.
 */
type LastroTerritorial = {
  total: number;
  territoriosSomados: number;
  /** Ações vivas sem território: não há população a que atribuí-las. */
  semTerritorio: number;
  /** Territórios com ação, mas sem população cadastrada no catálogo. */
  semPopulacao: string[];
};

export function lastroTerritorial(acoes: Acao[], bairros: BairroView[]): LastroTerritorial {
  const vivas = acoes.filter((a) => derivarStatus(a.status) !== 'cancelada');
  const semTerritorio = vivas.filter((a) => !a.bairro?.trim()).length;

  const nomes = [
    ...new Set(vivas.map((a) => a.bairro?.trim()).filter((n): n is string => !!n)),
  ];
  const populacaoPorNome = new Map(bairros.map((b) => [b.n.trim().toLowerCase(), b.pop || 0]));

  let total = 0;
  const semPopulacao: string[] = [];
  for (const nome of nomes) {
    const pop = populacaoPorNome.get(nome.toLowerCase());
    if (pop) total += pop;
    else semPopulacao.push(nome);
  }

  return {
    total,
    territoriosSomados: nomes.length - semPopulacao.length,
    semTerritorio,
    semPopulacao,
  };
}

type FormAcao = {
  titulo: string;
  tipo: string;
  bairro: string;
  tema: string;
  status: StatusAcao;
  progresso: number;
  valor: number;
  emenda_id: string;
  solicitacoes_resolvidas: number;
  alcance: number;
  marco: string;
};

const FORM_VAZIO: FormAcao = {
  titulo: '',
  tipo: 'Obra',
  bairro: '',
  // Sem tema pré-escolhido: cravar um ("Saúde básica" era o padrão) atribui à
  // ação um assunto que ninguém informou, e é esse campo que a leitura
  // estratégica dos painéis cruza com a demanda.
  tema: '',
  status: 'planejada',
  progresso: 0,
  valor: 0,
  emenda_id: '',
  solicitacoes_resolvidas: 0,
  alcance: 0,
  marco: '',
};

export function AcoesPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();
  /**
   * Catálogo de temas do mandato — o MESMO que Solicitações usa no campo Tema.
   * Era uma constante de 7 itens aqui dentro; como nenhum deles coincidia com as
   * categorias que as solicitações usam, a nota "Tema recorrente sem ação" dos
   * painéis (proto:1208) nunca conseguia encontrar a ação existente.
   */
  const { temas, isLoading: temasCarregando, isError: temasErro } = useTemas();

  const [statusFilter, setStatusFilter] = useState<StatusVisivel | ''>('');
  const [tipoFilter, setTipoFilter] = useState('');

  const [selecionada, setSelecionada] = useState<Acao | null>(null);
  const [modalForm, setModalForm] = useState<'nova' | 'editar' | null>(null);
  const [form, setForm] = useState<FormAcao>(FORM_VAZIO);

  const [tagFiltro, setTagFiltro] = useState('');
  const [mensagem, setMensagem] = useState('');
  /**
   * Progresso em edição na ficha. Começa no valor do banco e só vira PUT quando
   * o usuário confirma — arrastar um slider não pode disparar uma gravação por
   * pixel percorrido.
   */
  const [progressoEdit, setProgressoEdit] = useState(0);
  /**
   * Sempre que a ficha passa a olhar outro registro — clique no cartão,
   * deep-link ou retorno do PUT — o controle volta ao valor gravado. Sincronizar
   * só no clique deixaria o slider mostrando o progresso da ação anterior.
   */
  const idFicha = selecionada?.id;
  const progressoGravado = selecionada?.progresso ?? 0;
  useEffect(() => {
    setProgressoEdit(progressoGravado);
  }, [idFicha, progressoGravado]);

  /**
   * `?nova=1&tema=<tema>` abre o formulário de nova ação já com o tema.
   *
   * É o destino do link "Criar ação" da leitura estratégica dos painéis de
   * Solicitações (proto:1208): o link nasce apontando um tema com demanda e
   * nenhuma ação em curso — chegar aqui com o formulário fechado perderia
   * exatamente essa informação.
   *
   * O tema entra no formulário exatamente como veio na URL. Quando o campo era
   * um `<select>` fechado sobre uma lista fixa, um tema fora dela não casava
   * nenhuma `<option>`: a tela exibia o primeiro item e salvava outro valor. O
   * campo hoje é o mesmo `input` + `datalist` de Solicitações — o que está na
   * tela é o que vai para a API.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!searchParams.get('nova')) return;
    const tema = searchParams.get('tema');
    setForm({ ...FORM_VAZIO, ...(tema ? { tema } : {}) });
    setModalForm('nova');
    const p = new URLSearchParams(searchParams);
    p.delete('nova');
    p.delete('tema');
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  /**
   * Uma consulta só, sem filtro de status no servidor.
   *
   * `GET /gab/acoes?status=` aceita UM valor do vocabulário de cinco do banco;
   * "Pendente" é a união de três deles, e não há como pedir isso numa chamada.
   * Filtrar aqui, sobre a lista completa, mantém KPI e grade lendo exatamente o
   * mesmo conjunto — quando eram duas consultas, o KPI e a grade podiam
   * discordar enquanto a segunda ainda estava no ar.
   */
  const { data: todas = [], isLoading, isError, error } = useQuery({
    queryKey: ['acoes'],
    queryFn: () => acoesApi.list({}, token),
    enabled: !!token,
  });

  const acoes = todas.filter(
    (a) =>
      (!statusFilter || derivarStatus(a.status) === statusFilter) &&
      (!tipoFilter || a.tipo === tipoFilter),
  );

  /**
   * Deep-link da paleta de comandos: `/acoes?acao=<id>` abre a ficha, na mesma
   * convenção de `/contatos?contato=<id>` (Task 5.2). A lista da tela é
   * filtrada, então quando a ação não está nela ela é buscada pelo id.
   */
  const acaoAlvo = searchParams.get('acao') ?? '';
  const acaoNaLista = acoes.find((a) => a.id === acaoAlvo);

  const { data: acaoPorLink } = useQuery({
    queryKey: ['acoes', 'link', acaoAlvo],
    queryFn: () => acoesApi.get(acaoAlvo, token),
    enabled: !!token && !!acaoAlvo && !acaoNaLista,
  });

  const acaoDoLink = acaoNaLista ?? acaoPorLink;

  useEffect(() => {
    if (!acaoAlvo || !acaoDoLink) return;
    setSelecionada(acaoDoLink);
    const p = new URLSearchParams(searchParams);
    p.delete('acao');
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acaoAlvo, acaoDoLink]);

  const { data: emendas = [] } = useQuery({
    queryKey: ['emendas'],
    queryFn: () => emendasApi.list(token),
    enabled: !!token,
  });

  // Contatos com opt-in do território da ação aberta, para comunicação manual
  const { data: contatosTerritorio } = useQuery({
    queryKey: ['contatos', 'acao', selecionada?.bairro, tagFiltro],
    queryFn: () =>
      contatosApi.list(
        {
          bairro: selecionada?.bairro || undefined,
          tag: tagFiltro || undefined,
          base_legal: 'optin',
          limit: 100,
        },
        token,
      ),
    enabled: !!token && !!selecionada,
  });

  const contatos = contatosTerritorio?.items ?? [];

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['acoes'] });
    queryClient.invalidateQueries({ queryKey: ['bairros'] });
  };

  const createMutation = useMutation({
    mutationFn: (dados: Partial<Acao>) => acoesApi.create(dados, token),
    onSuccess: () => {
      invalidar();
      toast('Ação do mandato criada.');
      setModalForm(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao criar a ação.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Acao> }) =>
      acoesApi.update(id, dados, token),
    onSuccess: (a) => {
      invalidar();
      toast('Ação atualizada.');
      setSelecionada(a);
      setModalForm(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a ação.')),
  });

  const concluirMutation = useMutation({
    mutationFn: ({ id, comunicado }: { id: string; comunicado?: string }) =>
      acoesApi.concluir(id, comunicado, token),
    onSuccess: (a) => {
      invalidar();
      toast('Ação concluída. Comunicado registrado na ficha.');
      setSelecionada(a);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao concluir a ação.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => acoesApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Ação removida.');
      setSelecionada(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover a ação.')),
  });

  const contarVisivel = (v: StatusVisivel) =>
    todas.filter((a) => derivarStatus(a.status) === v).length;

  const realizadas = contarVisivel('realizada');
  const pendentes = contarVisivel('pendente');
  // Marco de ação cancelada não entra: é marco que ninguém vai cumprir.
  const pendentesComMarco = todas.filter(
    (a) => derivarStatus(a.status) === 'pendente' && !!a.marco?.trim(),
  ).length;

  const lastro = lastroTerritorial(todas, bairros);

  const comValor = todas.filter((a) => Number(a.valor) > 0);
  const totalInvestimento = comValor.reduce((a, x) => a + (Number(x.valor) || 0), 0);

  const abrirFicha = (a: Acao) => {
    setSelecionada(a);
    setTagFiltro('');
    setMensagem(
      `Olá! O mandato da Vereadora Ana Silva informa sobre a ação "${a.titulo}" em ${a.bairro || 'nosso município'}: status atualizado para ${rotuloBanco(a.status)}. Estamos à disposição!`,
    );
  };

  const abrirNova = () => {
    // `FORM_VAZIO` puro, igual ao caminho do deep-link: pré-selecionar
    // `bairros[0]` dava à ação um território que ninguém escolheu — o primeiro
    // da lista — e o select já tem "Toda a cidade" como opção honesta.
    setForm(FORM_VAZIO);
    setModalForm('nova');
  };

  const abrirEdicao = () => {
    if (!selecionada) return;
    setForm({
      titulo: selecionada.titulo,
      tipo: selecionada.tipo,
      bairro: selecionada.bairro || '',
      tema: selecionada.tema || '',
      status: selecionada.status,
      progresso: selecionada.progresso,
      valor: Number(selecionada.valor) || 0,
      emenda_id: selecionada.emenda_id || '',
      solicitacoes_resolvidas: selecionada.solicitacoes_resolvidas,
      alcance: selecionada.alcance || 0,
      marco: selecionada.marco || '',
    });
    setModalForm('editar');
  };

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.titulo.trim()) {
      toast('O título da ação é obrigatório.');
      return;
    }
    const dados: Partial<Acao> = {
      titulo: form.titulo.trim(),
      tipo: form.tipo,
      bairro: form.bairro || undefined,
      tema: form.tema.trim() || undefined,
      status: form.status,
      progresso: form.progresso,
      valor: form.valor || undefined,
      emenda_id: form.emenda_id || undefined,
      solicitacoes_resolvidas: form.solicitacoes_resolvidas,
      alcance: form.alcance || undefined,
      marco: form.marco.trim() || undefined,
    };

    if (modalForm === 'nova') createMutation.mutate(dados);
    else if (selecionada) updateMutation.mutate({ id: selecionada.id, dados });
  };

  const excluir = () => {
    if (!selecionada) return;
    if (window.confirm(`Remover a ação "${selecionada.titulo}"?`)) {
      deleteMutation.mutate(selecionada.id);
    }
  };

  const abrirWhatsApp = (tel: string | undefined, nome: string) => {
    if (!tel) return;
    const texto = mensagem.replace('Olá!', `Olá, ${nome.split(' ')[0]}!`);
    window.open(`https://wa.me/${tel.replace(/\D/g, '')}?text=${encodeURIComponent(texto)}`, '_blank');
  };

  const codigoEmenda = (id?: string) => emendas.find((e) => e.id === id)?.codigo;

  return (
    <div className="page" id="p-acoes">
      {/* Cabeçalho */}
      <div className="head">
        <div>
          <h1>Ações parlamentares</h1>
          <p>
            {/*
              proto:1380 fecha com "e às pessoas afetadas", e o texto que
              estava aqui prometia "ciclo completo: demanda popular → execução →
              comunicação aos moradores". Nenhuma das duas metades existe: não
              há `solicitacao_id` em `gab.acao`, nem `acao_id` em
              `gab.solicitacao`, nem tabela de ligação — a lacuna está declarada
              no KPI "Solicitações resolvidas por ações", logo abaixo —, e
              `acao.comunicado` é texto livre que fica na ficha, sem enfileirar
              mensagem e sem gatilho de régua correspondente.
            */}
            Tudo que o mandato fez ou se comprometeu a fazer (obras, emendas, projetos, eventos,
            visitas, ofícios), ligado a uma localidade e a uma necessidade. Status: Realizada ou
            Pendente.
          </p>
        </div>
        <div className="act">
          <button className="btn pri" onClick={abrirNova}>
            + Nova ação
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters">
        <span
          className={`chip neutral chipbtn ${statusFilter === '' ? 'on' : ''}`}
          onClick={() => setStatusFilter('')}
        >
          Todas ({todas.length})
        </span>
        {/*
          Três chips, não cinco: a leitura é Realizada/Pendente (proto:1374) e
          `Cancelada` fica à parte. As três contagens somam o total de "Todas" —
          se `cancelada` fosse ocultada, a aritmética da barra deixaria de fechar
          e a ação sumiria de quem precisa auditá-la ou reabri-la.
        */}
        {STATUS_VISIVEIS.map((s) => (
          <span
            key={s.v}
            className={`chip neutral chipbtn ${statusFilter === s.v ? 'on' : ''}`}
            onClick={() => setStatusFilter(s.v)}
          >
            {s.l} ({contarVisivel(s.v)})
          </span>
        ))}

        <span style={{ flex: 1 }} />

        <select className="sel" value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)}>
          <option value="">Todos os tipos</option>
          {TIPOS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {/* 4 KPIs (proto:1382) */}
      <div className="grid g4" style={{ marginTop: 8 }}>
        <div className="card kpi">
          <div className="l">Realizadas</div>
          <div className="v" style={{ color: 'var(--good)' }}>{realizadas}</div>
          <div className="d">entregas comunicadas aos moradores</div>
        </div>

        <div className="card kpi">
          <div className="l">Pendentes</div>
          <div className="v" style={{ color: 'var(--warn)' }}>{pendentes}</div>
          <div className="d">com marco definido: {pendentesComMarco}</div>
        </div>

        {/*
          LACUNA DECLARADA, no lugar de um número.

          O KPI do protótipo soma `acao.resolve`. No sistema esse campo é
          `gab.acao.solicitacoes_resolvidas`, um inteiro DIGITADO na ficha: não
          aponta para solicitação nenhuma. Não existe `solicitacao_id` em
          `gab.acao`, não existe `acao_id` em `gab.solicitacao` e não existe
          tabela de ligação — conferido nas entidades e nas 13 migrations.
          Somá-lo aqui produziria um indicador que ninguém consegue auditar.

          Contar por `tema` também não serve. O pareamento por tema existe em
          `PainelSolicitacoes.tsx` e é legítimo lá, onde sustenta uma afirmação
          EXISTENCIAL ("há alguma ação neste tema?"), que tolera aproximação.
          Aqui a afirmação é SOMATÓRIA: creditaria a mesma solicitação a todas
          as ações do tema e contaria demanda que ninguém resolveu. Seria trocar
          um número não auditável por outro — pior, porque pareceria medido.
        */}
        <div className="card kpi">
          <div className="l">
            Solicitações resolvidas por ações{' '}
            <Info titulo="Por que este indicador não tem número">
              <code>gab.acao</code> não tem <code>solicitacao_id</code>, <code>gab.solicitacao</code>{' '}
              não tem <code>acao_id</code> e não existe tabela de ligação. O contador da ficha é
              informado pelo gabinete, sem referente: somá-lo daria um indicador que ninguém
              consegue auditar.
            </Info>
          </div>
          {/*
            O aviso fica visível: sem ele, um cartão de KPI sem número se lê
            como "zero solicitações resolvidas". O método vai no `Info`.
          */}
          <div className="note warn" style={{ marginTop: 6, fontSize: 12 }}>
            Sem número: não há vínculo entre ação e solicitação no banco.
          </div>
        </div>

        <div className="card kpi">
          <div className="l">
            Moradores nos territórios com ação{' '}
            <Info titulo="Como os moradores são contados">
              Soma a população IBGE dos territórios distintos que têm ação, uma vez cada. O campo
              “Alcance” da ficha é digitado e não entra na conta.
            </Info>
          </div>
          {lastro.territoriosSomados > 0 ? (
            <>
              <div className="v">{num(lastro.total)}</div>
              <div className="d">
                população IBGE dos {lastro.territoriosSomados} território(s) distintos com ação;
                não é alcance verificado
                {lastro.semTerritorio > 0
                  ? `; ${lastro.semTerritorio} ${lastro.semTerritorio === 1 ? 'ação sem território' : 'ações sem território'} fora da conta`
                  : ''}
                {lastro.semPopulacao.length > 0
                  ? `; sem população cadastrada: ${lastro.semPopulacao.join(', ')}`
                  : ''}
              </div>
            </>
          ) : (
            <div className="note warn" style={{ marginTop: 6, fontSize: 12 }}>
              Sem número: nenhum território com ação tem população cadastrada em Territórios.
              {lastro.semTerritorio > 0
                ? ` ${lastro.semTerritorio} ${lastro.semTerritorio === 1 ? 'ação sem território' : 'ações sem território'} também ficariam fora.`
                : ''}
            </div>
          )}
        </div>
      </div>

      {/*
        O investimento saiu do bloco de KPIs para caber os quatro do protótipo,
        mas não foi descartado: é soma de `acao.valor`, dado real e auditável
        registro a registro.
      */}
      <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '8px 0 0' }}>
        Investimento vinculado: <b>{brl(totalInvestimento)}</b>; soma do valor informado em{' '}
        {comValor.length} de {todas.length} ações.
      </p>

      {/* Grade de ações */}
      <div className="grid g3" style={{ marginTop: 12 }}>
        {acoes.map((a) => {
          const visivel = derivarStatus(a.status);
          const concluida = visivel === 'realizada';
          const cancelada = visivel === 'cancelada';
          const emCurso = a.status === 'em_andamento' || a.status === 'em_tramitacao';
          const cor = concluida ? 'var(--good)' : cancelada ? 'var(--line)' : emCurso ? 'var(--warn)' : 'var(--line)';

          return (
            <div
              key={a.id}
              className={`card stripe ${concluida ? 'good' : cancelada ? '' : emCurso ? 'warn' : ''}`}
              style={{
                cursor: 'pointer',
                borderLeft: `4px solid ${cor}`,
                opacity: cancelada ? 0.72 : undefined,
              }}
              onClick={() => abrirFicha(a)}
            >
              <div className="filters" style={{ margin: 0, justifyContent: 'space-between' }}>
                {/*
                  Rótulo DERIVADO. O valor guardado continua sendo um dos cinco e
                  aparece por extenso na ficha — aqui a leitura é a do protótipo.
                */}
                <span className={`chip ${concluida ? 'good' : cancelada ? 'neutral' : emCurso ? 'warn' : 'neutral'}`}>
                  {visivel ? rotuloVisivel(visivel) : rotuloBanco(a.status)}
                  {a.status === 'em_andamento' ? ` · ${a.progresso}%` : ''}
                </span>
                <span className="chip neutral">{a.tipo}</span>
              </div>

              <h2 style={{ margin: '8px 0 4px', fontSize: 15 }}>{a.titulo}</h2>
              <p style={{ fontSize: 13, margin: 0, color: 'var(--ink2)' }}>
                {a.bairro || 'Toda a cidade'} · {a.tema || 'Sem tema'}
                {Number(a.valor) ? ` · R$ ${(Number(a.valor) / 1000).toFixed(0)} mil` : ''}
              </p>

              {a.status === 'em_andamento' && (
                <div className="bar" style={{ marginTop: 10 }}>
                  <i style={{ width: `${a.progresso}%`, background: 'var(--warn)' }} />
                </div>
              )}

              <dl className="dl" style={{ marginTop: 12, fontSize: 12 }}>
                {/* Os dois primeiros são campos digitados na ficha, não medições —
                    o rótulo diz isso para que não sejam lidos como resultado apurado. */}
                <dt>Resolve (informado)</dt>
                <dd>{a.solicitacoes_resolvidas} solicitações</dd>
                <dt>Alcance (informado)</dt>
                <dd className="num">
                  {a.alcance ? `${num(a.alcance)} moradores` : '—'}
                </dd>
                <dt>{concluida ? 'Comunicado' : cancelada ? 'Marco (cancelada)' : 'Próximo marco'}</dt>
                <dd>
                  {concluida ? (
                    <span className="chip good">{a.comunicado || 'Registrado'}</span>
                  ) : (
                    a.marco || '—'
                  )}
                </dd>
              </dl>
            </div>
          );
        })}

        {isLoading && (
          <div className="empty" style={{ gridColumn: '1 / -1' }}>
            Carregando ações...
          </div>
        )}
        {isError && !isLoading && (
          <div className="empty" style={{ gridColumn: '1 / -1' }}>
            Não foi possível carregar as ações: {erroDe(error, 'erro de comunicação com a API.')}
          </div>
        )}
        {!isLoading && !isError && acoes.length === 0 && (
          <div className="empty" style={{ gridColumn: '1 / -1' }}>
            Nenhuma ação encontrada com esses filtros.
          </div>
        )}
      </div>

      {/* Drawer: ficha da ação */}
      <Drawer
        aberto={!!selecionada}
        titulo={selecionada?.titulo || ''}
        aoFechar={() => setSelecionada(null)}
      >
        {selecionada && (
          <>
            <div className="filters" style={{ marginBottom: 14 }}>
              <span className="chip pri">
                {derivarStatus(selecionada.status)
                  ? rotuloVisivel(derivarStatus(selecionada.status)!)
                  : rotuloBanco(selecionada.status)}
              </span>
              <span className="chip neutral">{selecionada.tipo}</span>
              {selecionada.tema && <span className="chip neutral">{selecionada.tema}</span>}
            </div>

            <dl className="dl" style={{ marginBottom: 16 }}>
              {/*
                O status guardado continua à vista: derivar na leitura não pode
                custar a informação que alguém digitou. `gab.acao.status` segue
                com os cinco valores, e é este o que a API recebe de volta.
              */}
              <dt>Status no sistema</dt>
              <dd>{rotuloBanco(selecionada.status)}</dd>
              <dt>Território</dt>
              <dd>{selecionada.bairro || 'Toda a cidade'}</dd>
              {/*
                Os dois campos abaixo continuam no banco e na ficha — nada foi
                apagado. O que mudou é o rótulo: são declarações do gabinete,
                não medições, e é por isso que nenhum KPI os soma.
              */}
              <dt>Solicitações resolvidas (informado pelo gabinete)</dt>
              <dd>{selecionada.solicitacoes_resolvidas} solicitações</dd>
              <dt>Alcance estimado (número digitado na ficha)</dt>
              <dd className="num">{selecionada.alcance ? num(selecionada.alcance) : '—'}</dd>
              <dt>Investimento</dt>
              <dd className="num">
                {Number(selecionada.valor) ? brl(Number(selecionada.valor)) : 'Sem valor vinculado'}
              </dd>
              <dt>Emenda</dt>
              <dd>
                {selecionada.emenda_id ? (
                  <Link to={`/emendas?emenda=${selecionada.emenda_id}`}>
                    {codigoEmenda(selecionada.emenda_id) || 'Ver emenda'}
                  </Link>
                ) : (
                  'Sem emenda vinculada'
                )}
              </dd>
              {selecionada.concluida_em && (
                <>
                  <dt>Concluída em</dt>
                  <dd>{new Date(selecionada.concluida_em).toLocaleDateString('pt-BR')}</dd>
                </>
              )}
            </dl>

            {/*
              Progresso: o único dos cinco itens do drawer do protótipo
              (proto:814) que o banco sustenta inteiro. `gab.acao.progresso` é
              `int` 0-100 e o PUT o aceita — então o controle grava de verdade,
              e sozinho, sem arrastar junto o resto da ficha.
            */}
            <div className="field" style={{ marginBottom: 16 }}>
              <label htmlFor="acao-progresso">Progresso (%)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  id="acao-progresso"
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  style={{ flex: 1 }}
                  value={progressoEdit}
                  onChange={(e) => setProgressoEdit(Number(e.target.value))}
                />
                <b className="num" style={{ minWidth: 44, textAlign: 'right' }}>
                  {progressoEdit}%
                </b>
                <button
                  className="btn sm"
                  disabled={progressoEdit === progressoGravado || updateMutation.isPending}
                  onClick={() =>
                    updateMutation.mutate({
                      id: selecionada.id,
                      dados: { progresso: progressoEdit },
                    })
                  }
                >
                  Salvar progresso
                </button>
              </div>
              <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                Gravado em <span className="mono">gab.acao.progresso</span>. É um número que o
                gabinete digita; não sai de marco, empenho nem medição de obra.
              </small>
            </div>

            {/*
              Linha do tempo possível. `gab.solicitacao` tem
              `solicitacao-evento` e por isso a ficha dela mostra quem mudou o
              quê e quando; `gab.acao` não tem tabela equivalente, e não há rota
              de auditoria exposta ao front. Em vez de um histórico fabricado,
              ficam as datas que o próprio registro carrega — e a declaração do
              que não foi gravado entre elas.
            */}
            <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>
              Linha do tempo{' '}
              <Info titulo="O que a ficha da ação não guarda">
                <b>Histórico de alterações:</b> não há tabela de eventos para{' '}
                <span className="mono">gab.acao</span>, só <span className="mono">gab.solicitacao</span>{' '}
                tem (<span className="mono">solicitacao-evento.entity.ts</span>). As três datas
                acima são o que o registro guarda; quem mudou o status ontem não ficou gravado em
                lugar nenhum que a tela possa ler.
                <br />
                <b>Anexos:</b> <span className="mono">DonoAnexo</span> é{' '}
                <span className="mono">'solicitacao' | 'contato' | 'visita' | 'fonte'</span>.{' '}
                <span className="mono">acao</span> não está na lista, nem no controller: um upload
                aqui seria recusado pelo servidor.
                <br />
                <b>Régua:</b> concluir uma ação não dispara régua nenhuma. Os gatilhos do servidor
                são <span className="mono">contato.aniversario</span> e{' '}
                <span className="mono">solicitacao.concluida</span>. Não há gatilho de ação.
              </Info>
            </h3>
            <div className="tl" data-testid="linha-do-tempo-acao" style={{ marginBottom: 8 }}>
              <div className="tli">
                <div className="d">{formatarData(selecionada.created_at)}</div>
                <div className="t">
                  <b>Ação cadastrada</b>
                </div>
              </div>
              {selecionada.concluida_em && (
                <div className="tli">
                  <div className="d">{formatarData(selecionada.concluida_em)}</div>
                  <div className="t">
                    <b>Marcada como realizada</b>
                  </div>
                  {selecionada.comunicado && <div className="desc">{selecionada.comunicado}</div>}
                </div>
              )}
              <div className="tli">
                <div className="d">{formatarData(selecionada.updated_at)}</div>
                <div className="t">
                  <b>Última alteração na ficha</b>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <button className="btn sm" onClick={abrirEdicao}>
                ✏️ Editar
              </button>
              {selecionada.status !== 'concluida' && (
                <button
                  className="btn sm pri"
                  disabled={concluirMutation.isPending}
                  onClick={() =>
                    concluirMutation.mutate({ id: selecionada.id, comunicado: mensagem })
                  }
                >
                  ✓ Concluir ação
                </button>
              )}
              <button
                className="btn sm"
                style={{ color: 'var(--crit)', marginLeft: 'auto' }}
                onClick={excluir}
                disabled={deleteMutation.isPending}
              >
                Excluir
              </button>
            </div>

            {/* Comunicação com moradores do território */}
            <div className="card flat" style={{ background: 'var(--surface2)', padding: 14, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 14 }}>🎯 Contatos interessados no território</h3>
                <span className="chip neutral" style={{ fontSize: 11 }}>
                  {contatos.length} com opt-in
                </span>
              </div>
              {/*
                "Comunicar moradores" do protótipo (proto:814). O caminho existe,
                mas não a partir daqui: o disparo em lote é o motor de campanhas,
                que recorta o público por `base_legal = 'optin'`
                (`campanhas.service.ts`, `calcularPublicoAlvo`) e ainda passa por
                aprovação antes de sair. O link leva ao compositor, com a
                condição dita; prometer envio neste botão seria prometer o que
                esta tela não faz.
              */}
              <p style={{ fontSize: 12, color: 'var(--ink2)', margin: '0 0 10px' }}>
                Somente contatos com consentimento registrado. Aqui o envio é manual, um a um, pelo
                WhatsApp. Para falar com o território inteiro de uma vez,{' '}
                <Link to="/campanhas">Comunicar moradores</Link> abre o compositor de campanhas:
                o público é recortado por opt-in e a campanha só sai depois de aprovada.
              </p>

              <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                {['', 'Saúde', 'Educação', 'Liderança', 'Comércio'].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`chip chipbtn ${tagFiltro === tag ? 'pri' : 'neutral'}`}
                    style={{ fontSize: 11 }}
                    onClick={() => setTagFiltro(tag)}
                  >
                    {tag || 'Todas as tags'}
                  </button>
                ))}
              </div>

              <div className="field" style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11 }}>Mensagem para envio</label>
                <textarea
                  className="in sm"
                  style={{ minHeight: 60, fontSize: 12 }}
                  value={mensagem}
                  onChange={(e) => setMensagem(e.target.value)}
                />
              </div>

              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                {contatos.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 0',
                      borderBottom: '1px solid var(--line)',
                      fontSize: 12,
                    }}
                  >
                    <div>
                      <b>{c.nome}</b>
                      <div style={{ color: 'var(--ink3)' }}>
                        {c.bairro} · {(c.tags || []).join(', ') || 'sem tags'}
                      </div>
                    </div>
                    <button
                      className="btn sm"
                      disabled={!c.telefone_e164}
                      onClick={() => abrirWhatsApp(c.telefone_e164, c.nome)}
                    >
                      💬
                    </button>
                  </div>
                ))}
                {contatos.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--ink3)', padding: '8px 0' }}>
                    Nenhum contato com opt-in nesse recorte.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </Drawer>

      {/* Modal: nova / editar ação */}
      {modalForm && (
        <form onSubmit={submeter}>
          <Drawer
            aberto
            titulo={modalForm === 'nova' ? 'Nova ação do mandato' : 'Editar ação'}
            aoFechar={() => setModalForm(null)}
            rodape={
              <>
                <button type="button" className="btn" onClick={() => setModalForm(null)}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn pri"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Salvando...'
                    : modalForm === 'nova'
                    ? 'Criar ação'
                    : 'Salvar alterações'}
                </button>
              </>
            }
          >
          <div className="field">
            <label>Título da ação *</label>
            <input
              className="in"
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              placeholder="Ex: Iluminação LED nas Ruas 10 a 14"
              required
            />
          </div>

          <div className="grid g2">
            <div className="field">
              <label>Tipo</label>
              <select
                className="in"
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              >
                {TIPOS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Tema</label>
              <input
                className="in"
                list="temas-acao"
                value={form.tema}
                onChange={(e) => setForm({ ...form, tema: e.target.value })}
                placeholder="Categoria da demanda que esta ação atende"
              />
              <datalist id="temas-acao">
                {temas.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
              {/* Lacuna declarada: sem catálogo, o campo continua aceitando
                  texto livre, mas o usuário precisa saber que um tema digitado
                  fora do catálogo não cruza com a demanda nos painéis. Trazer
                  de volta uma lista fixa aqui recriaria o defeito em silêncio. */}
              {temasErro ? (
                <small style={{ fontSize: 11, color: 'var(--crit)' }}>
                  Não foi possível carregar o catálogo de temas do mandato. O campo aceita texto
                  livre, mas só um tema idêntico ao das solicitações aparece como atendido nos
                  painéis.
                </small>
              ) : !temasCarregando && temas.length === 0 ? (
                <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                  Nenhum tema cadastrado em <Link to="/config">Configurações › Tipos</Link>{' '}
                  (Solicitações). Sem catálogo não há sugestão: o que for digitado só cruza com a
                  demanda nos painéis se for igual ao tema das solicitações.
                </small>
              ) : (
                <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                  Mesmo catálogo das solicitações: é por ele que os painéis dizem qual demanda
                  ainda não tem ação em curso.
                </small>
              )}
            </div>
          </div>

          <div className="grid g2">
            <div className="field">
              <label>Território</label>
              <select
                className="in"
                value={form.bairro}
                onChange={(e) => setForm({ ...form, bairro: e.target.value })}
              >
                <option value="">Toda a cidade</option>
                {bairros.map((b) => (
                  <option key={b.id} value={b.n}>
                    {b.n}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Status</label>
              <select
                className="in"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as StatusAcao })}
              >
                {/*
                  A ESCRITA continua com os cinco valores do banco. A leitura é
                  que colapsa em Realizada/Pendente/Cancelada — reduzir também o
                  formulário apagaria a diferença entre planejada, em tramitação
                  e em andamento, que é informação que o mandato usa.
                */}
                {STATUS_BANCO.map((s) => (
                  <option key={s} value={s}>
                    {ROTULO_BANCO[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid g2">
            <div className="field">
              <label>Progresso (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                className="in num"
                value={form.progresso}
                onChange={(e) => setForm({ ...form, progresso: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="field">
              <label>Valor (R$)</label>
              <input
                type="number"
                min={0}
                className="in num"
                value={form.valor}
                onChange={(e) => setForm({ ...form, valor: Number(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="grid g2">
            <div className="field">
              <label>Emenda que custeia</label>
              <select
                className="in"
                value={form.emenda_id}
                onChange={(e) => setForm({ ...form, emenda_id: e.target.value })}
              >
                <option value="">Sem emenda vinculada</option>
                {emendas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.codigo} · {e.titulo}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Solicitações resolvidas (anotação do gabinete)</label>
              <input
                type="number"
                min={0}
                className="in num"
                value={form.solicitacoes_resolvidas}
                onChange={(e) =>
                  setForm({ ...form, solicitacoes_resolvidas: Number(e.target.value) || 0 })
                }
              />
              <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                Fica registrado na ficha, mas não vira indicador: não há vínculo entre ação e
                solicitação no banco, então o número não pode ser conferido contra nada.
              </small>
            </div>
          </div>

          <div className="grid g2">
            <div className="field">
              <label>Alcance estimado (anotação do gabinete)</label>
              <input
                type="number"
                min={0}
                className="in num"
                value={form.alcance}
                onChange={(e) => setForm({ ...form, alcance: Number(e.target.value) || 0 })}
              />
              <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                Estimativa sua, guardada na ficha. O indicador de moradores usa a população IBGE do
                território, não este campo.
              </small>
            </div>
            <div className="field">
              <label>Próximo marco</label>
              <input
                className="in"
                value={form.marco}
                onChange={(e) => setForm({ ...form, marco: e.target.value })}
                placeholder="Ex: Licitação em 27/08"
              />
            </div>
          </div>

          </Drawer>
        </form>
      )}
    </div>
  );
}
